import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { format, addDays } from 'date-fns';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Loader2, AlertCircle, Calendar, RefreshCw, Activity } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useSchedule } from '../hooks/useSchedule';
import { useScheduleSlots } from '../hooks/useScheduleSlots';
import { useScheduleAvailability } from '../hooks/useScheduleAvailability';
import { useWebSocket, type WebSocketMessage } from '../hooks/useWebSocket';
import { apiFetch } from '../lib/api';
import { GanttChart, GanttLegend } from '../components/GanttChart';
import { Toast, type ToastType } from '../components/Toast';

interface ScheduleHealthData {
  overallScore: number;
  status: 'healthy' | 'warning' | 'critical';
  tasksScheduled: number;
  tasksUnassignable: number;
  priorityInversions: number;
  avgUtilizationPercent: number;
  gapsFilled: number;
  makespanDays: number;
  onTimeRate: { onTrack: number; atRisk: number; late: number; total: number };
}

function useScheduleHealth() {
  return useQuery({
    queryKey: ['schedule-health'],
    queryFn: () => apiFetch<{ data: ScheduleHealthData }>('/api/v1/dashboards/schedule-health').then((r) => r.data),
    staleTime: 60000,
  });
}

const statusColors = {
  healthy: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  warning: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
  critical: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
} as const;

function ScheduleHealthCard() {
  const [expanded, setExpanded] = useState(false);
  const { data: health, isLoading, error } = useScheduleHealth();

  if (isLoading) {
    return (
      <div className="bg-background rounded-lg border border-border p-3 flex items-center gap-2 text-sm text-foreground-muted">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading schedule health...
      </div>
    );
  }

  if (error || !health) return null;

  const colors = statusColors[health.status];

  return (
    <div className="bg-background rounded-lg border border-border">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-background-subtle transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <Activity className="h-4 w-4 text-foreground-muted" />
          <span className="text-sm font-medium text-foreground">Schedule Health</span>
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
            {health.overallScore}/100
          </span>
        </div>
        <div className="flex items-center gap-4">
          {!expanded && (
            <div className="hidden sm:flex items-center gap-4 text-xs text-foreground-muted">
              <span>{health.tasksScheduled} scheduled</span>
              <span>{health.avgUtilizationPercent}% util</span>
              {health.tasksUnassignable > 0 && (
                <span className="text-amber-600">{health.tasksUnassignable} unassignable</span>
              )}
              {health.priorityInversions > 0 && (
                <span className="text-amber-600">{health.priorityInversions} inversions</span>
              )}
            </div>
          )}
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-foreground-muted" />
          ) : (
            <ChevronDown className="h-4 w-4 text-foreground-muted" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <MetricCell label="Tasks Scheduled" value={health.tasksScheduled} />
          <MetricCell label="Unassignable" value={health.tasksUnassignable} warn={health.tasksUnassignable > 0} />
          <MetricCell label="Priority Inversions" value={health.priorityInversions} warn={health.priorityInversions > 0} />
          <MetricCell label="Avg Utilization" value={`${health.avgUtilizationPercent}%`} />
          <MetricCell label="Gaps Filled" value={health.gapsFilled} />
          <MetricCell label="On-Time" value={health.onTimeRate.total > 0 ? `${Math.round((health.onTimeRate.onTrack / health.onTimeRate.total) * 100)}%` : 'N/A'} />
        </div>
      )}
    </div>
  );
}

function MetricCell({ label, value, warn }: { label: string; value: string | number; warn?: boolean }) {
  return (
    <div className="text-center">
      <div className={`text-lg font-semibold ${warn ? 'text-amber-600' : 'text-foreground'}`}>
        {value}
      </div>
      <div className="text-xs text-foreground-muted">{label}</div>
    </div>
  );
}

type ColorByOption = 'laborBucket' | 'status';
type ViewRange = 'next7' | 'next14';

export function SchedulePage() {
  // Default to today for "Next 7/14 Days" views
  const [baseDate, setBaseDate] = useState(() => new Date());
  const [viewRange, setViewRange] = useState<ViewRange>('next7');
  const [colorBy, setColorBy] = useState<ColorByOption>('laborBucket');

  // Toast state for real-time updates
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const lastScheduleVersion = useRef<number | null>(null);

  // Handle WebSocket messages for schedule updates
  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    if (message.type === 'schedule_updated') {
      setToast({ message: 'Schedule updated', type: 'update' });
    }
  }, []);

  // Subscribe to schedule channel (note: AppLayout already subscribes, but we add custom handler)
  const channels = useMemo(() => ['schedule'], []);
  useWebSocket(channels, { onMessage: handleWebSocketMessage });

  // Calculate date range based on view
  const { startDate, endDate } = useMemo(() => {
    const start = baseDate;
    const end = addDays(baseDate, viewRange === 'next14' ? 13 : 6);
    return { startDate: start, endDate: end };
  }, [baseDate, viewRange]);

  // Fetch schedule data (tasks for backward compat + slots for Gantt)
  const startDateStr = format(startDate, 'yyyy-MM-dd');
  const endDateStr = format(endDate, 'yyyy-MM-dd');

  const { data, isLoading, error, refetch } = useSchedule({
    startDate: startDateStr,
    endDate: endDateStr,
    limit: 500,
  });

  const { data: slotsData, refetch: refetchSlots } = useScheduleSlots(startDateStr, endDateStr);
  const { data: availabilityData } = useScheduleAvailability(startDateStr, endDateStr);

  // Detect schedule version changes and show notification
  useEffect(() => {
    if (data?.scheduleVersion) {
      if (lastScheduleVersion.current !== null && lastScheduleVersion.current !== data.scheduleVersion) {
        setToast({ message: 'Schedule updated with latest changes', type: 'update' });
      }
      lastScheduleVersion.current = data.scheduleVersion;
    }
  }, [data?.scheduleVersion]);

  // Navigation handlers
  const periodDays = viewRange === 'next14' ? 14 : 7;

  function goToPreviousPeriod() {
    setBaseDate((d) => addDays(d, -periodDays));
  }

  function goToNextPeriod() {
    setBaseDate((d) => addDays(d, periodDays));
  }

  function goToToday() {
    setBaseDate(new Date());
  }

  async function handleRecalculate() {
    setIsRecalculating(true);
    try {
      await apiFetch('/api/v1/admin/trigger-recalculate', { method: 'POST' });
      setToast({ message: 'Recalculation triggered — schedule will update shortly', type: 'update' });
    } catch (err) {
      setToast({ message: 'Failed to trigger recalculation', type: 'error' });
    } finally {
      setIsRecalculating(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Schedule</h1>
          <p className="text-sm text-foreground-muted mt-1">
            Production schedule timeline
          </p>
        </div>

        {/* Schedule version + recalculate */}
        <div className="flex items-center gap-3">
          {data?.scheduleVersion && (
            <span className="text-xs text-foreground-muted">
              v{data.scheduleVersion}
            </span>
          )}
          <button
            onClick={handleRecalculate}
            disabled={isRecalculating}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-foreground-muted hover:text-foreground border border-border rounded-md hover:bg-background-subtle transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isRecalculating ? 'animate-spin' : ''}`} />
            Recalculate
          </button>
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-background rounded-lg border border-border p-4">
        {/* Date navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={goToPreviousPeriod}
            className="p-2 rounded-md hover:bg-background-subtle transition-colors"
            aria-label="Previous period"
          >
            <ChevronLeft className="h-5 w-5 text-foreground-muted" />
          </button>

          <button
            onClick={goToToday}
            className="px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-md transition-colors"
          >
            Today
          </button>

          <button
            onClick={goToNextPeriod}
            className="p-2 rounded-md hover:bg-background-subtle transition-colors"
            aria-label="Next period"
          >
            <ChevronRight className="h-5 w-5 text-foreground-muted" />
          </button>

          <div className="ml-2 flex items-center gap-2 text-sm font-medium text-foreground">
            <Calendar className="h-4 w-4 text-foreground-muted" />
            {format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}
          </div>
        </div>

        {/* View controls */}
        <div className="flex items-center gap-4">
          {/* View range selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-foreground-muted">View:</label>
            <select
              value={viewRange}
              onChange={(e) => setViewRange(e.target.value as ViewRange)}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="next7">Next 7 Days</option>
              <option value="next14">Next 14 Days</option>
            </select>
          </div>

          {/* Color by selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-foreground-muted">Color by:</label>
            <select
              value={colorBy}
              onChange={(e) => setColorBy(e.target.value as ColorByOption)}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="laborBucket">Labor Type</option>
              <option value="status">Status</option>
            </select>
          </div>
        </div>
      </div>

      {/* Schedule Health */}
      <ScheduleHealthCard />

      {/* Legend */}
      <div className="bg-background rounded-lg border border-border p-4">
        <GanttLegend colorBy={colorBy} tasks={data?.data} slots={slotsData?.data} />
      </div>

      {/* Gantt chart */}
      <div className="bg-background rounded-lg border border-border overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-danger" />
              <div>
                <h3 className="font-medium text-danger-foreground">
                  Failed to load schedule
                </h3>
                <p className="text-sm text-danger-foreground mt-1">
                  {error instanceof Error ? error.message : 'Unknown error'}
                </p>
              </div>
            </div>
            <button
              onClick={() => refetch()}
              className="mt-4 text-sm font-medium text-danger hover:underline"
            >
              Try again
            </button>
          </div>
        ) : (
          <GanttChart
            tasks={data?.data ?? []}
            slots={slotsData?.data}
            startDate={startDate}
            endDate={endDate}
            colorBy={colorBy}
            availability={availabilityData?.data}
          />
        )}
      </div>

      {/* Footer info */}
      {data && data.data.length > 0 && (
        <p className="text-sm text-foreground-muted text-center">
          Showing {data.data.length} task{data.data.length !== 1 ? 's' : ''} in this period
        </p>
      )}

      {/* Real-time update toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          action={{
            label: 'Refresh',
            onClick: () => {
              refetch(); refetchSlots();
              setToast(null);
            },
          }}
        />
      )}
    </div>
  );
}
